import { 
  API_CONFIG, 
  buildWCUrl, 
  getDefaultHeaders,
  type APIResponse
} from '../../config/api.enhanced';

// Types for catalog
export interface Category {
  id: number;
  name: string;
  slug: string;
  image: string;
  language: string;
  polylang_language: string;
}

export interface Trip {
  id: number;
  title: string;
  price: number;
  price_html: string;
  image: string;
  capacity: number;
  available_seats: number;
  booked_seats: number;
  min_booking?: number;
  allow_infants?: boolean;
  book_full_boat?: boolean;
  description?: string;
  duration?: string;
  // Tax display metadata (optional, provided by backend)
  tax_display_mode?: 'incl' | 'excl';
  price_includes_tax?: boolean;
  base_price_raw?: number;
  base_price_ex_tax?: number;
  base_price_inc_tax?: number;
}

export interface TimeSlot {
  label: string;
  value: string;
  remaining: number;
  status?: 'available' | 'full';
}

export interface Addon {
  id: number;
  title: string;
  description: string;
  price: {
    amount: number;
    formatted: string;
    currency: string;
  };
  image: string;
  in_stock: boolean;
}

export interface CategoriesResponse {
  data: {
    categories: Category[];
    total: number;
    language: string;
    filtered: boolean;
  };
  language: string;
  timestamp: string;
}

export interface TripsResponse {
  data: {
    success: boolean;
    category: {
      id: number;
      name: string;
      slug: string;
    };
    trips: Trip[];
    total: number;
    language: string;
    filtered_by_language: boolean;
  };
  message: string;
}

export interface TimeSlotsResponse {
  slots: TimeSlot[];
}

export interface AddonsResponse {
  data: {
    success: boolean;
    addons: Addon[];
    total: number;
    language: string;
    filtered_by_language: boolean;
  };
  message: string;
}

/**
 * Catalog Service
 * Handles all catalog-related API calls (categories, trips, slots, addons)
 */
class CatalogService {
  private language: string = API_CONFIG.DEFAULT_LANGUAGE;

  setLanguage(lang: string): void {
    if (API_CONFIG.SUPPORTED_LANGUAGES.includes(lang)) {
      this.language = lang;
    }
  }

  private async request<T = any>(
    url: string,
    options: RequestInit = {},
    useAuth: boolean = false
  ): Promise<APIResponse<T>> {
    try {
      const headers = {
        ...getDefaultHeaders(),
        ...options.headers,
      };

      if (useAuth) {
        const token = localStorage.getItem('auth_token');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

const response = await fetch(url, {
        ...options,
        headers,
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('Non-JSON response received:', {
          url,
          status: response.status,
          contentType
        });
        return {
          success: false,
          message: `Expected JSON but received ${contentType || 'unknown content type'}. Status: ${response.status}`,
        };
      }

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || `HTTP ${response.status}: ${response.statusText}`,
          data: data
        };
      }

      return {
        success: true,
        data,
        message: data.message,
        language: this.language,
        timestamp: new Date().toISOString()
      };

    } catch (error) {

      return {
        success: false,
        message: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Get all trip categories
   */
  async getCategories(): Promise<APIResponse<CategoriesResponse>> {
    const url = buildWCUrl(API_CONFIG.ENDPOINTS.BOAT_CATEGORIES, { lang: this.language });

const response = await this.request<CategoriesResponse>(url);
    
    if (response.success && response.data?.data?.categories) {

    }
    
    return response;
  }

  /**
   * Get trips by category ID
   */
  async getTrips(categoryId: number): Promise<APIResponse<TripsResponse>> {
    const url = buildWCUrl(`${API_CONFIG.ENDPOINTS.BOAT_TRIPS}/${categoryId}`, { lang: this.language });

const response = await this.request<TripsResponse>(url);
    
    if (response.success && response.data?.data?.trips) {

}
    
    return response;
  }

  /**
   * Get available time slots for a trip on a specific date
   */
  async getTimeSlots(productId: number, date: string): Promise<APIResponse<TimeSlotsResponse>> {
    const url = buildWCUrl(`${API_CONFIG.ENDPOINTS.BOAT_SLOTS}/${productId}`, { date });

const response = await this.request<TimeSlotsResponse>(url, {}, false);
    
    if (response.success && response.data?.slots) {

    }
    
    return response;
  }

  /**
   * Get all available addons
   */
  async getAddons(): Promise<APIResponse<AddonsResponse>> {
    const url = buildWCUrl(API_CONFIG.ENDPOINTS.BOAT_ADDONS, { lang: this.language });

const response = await this.request<AddonsResponse>(url);
    
    if (response.success && response.data?.data?.addons) {

    }
    
    return response;
  }

  /**
   * Search trips by keyword
   */
  async searchTrips(query: string, categoryId?: number): Promise<APIResponse<TripsResponse>> {
    const params: any = { 
      search: query,
      lang: this.language
    };
    
    if (categoryId) {
      params.category_id = categoryId;
    }
    
    const url = buildWCUrl(API_CONFIG.ENDPOINTS.BOAT_TRIPS, params);

return await this.request<TripsResponse>(url);
  }

  /**
   * Get trip details by ID
   */
  async getTripDetails(tripId: number): Promise<APIResponse<Trip>> {
    const url = buildWCUrl(`${API_CONFIG.ENDPOINTS.PRODUCTS}/${tripId}`, { lang: this.language });

return await this.request<Trip>(url);
  }

  /**
   * Get category details by ID
   */
  async getCategoryDetails(categoryId: number): Promise<APIResponse<Category>> {
    const url = buildWCUrl(`${API_CONFIG.ENDPOINTS.CATEGORIES}/${categoryId}`, { lang: this.language });

return await this.request<Category>(url);
  }

  /**
   * Get featured trips
   */
  async getFeaturedTrips(): Promise<APIResponse<TripsResponse>> {
    const url = buildWCUrl(API_CONFIG.ENDPOINTS.PRODUCTS, { 
      featured: true,
      lang: this.language
    });

return await this.request<TripsResponse>(url);
  }

  /**
   * Get trips by price range
   */
  async getTripsByPriceRange(minPrice: number, maxPrice: number): Promise<APIResponse<TripsResponse>> {
    const url = buildWCUrl(API_CONFIG.ENDPOINTS.PRODUCTS, {
      min_price: minPrice,
      max_price: maxPrice,
      lang: this.language
    });

return await this.request<TripsResponse>(url);
  }
}

// Create singleton instance
const catalogService = new CatalogService();

export default catalogService;
